import { IMultiSectionUnitConfiguration, IUnitBaseConfiguration } from "../../../../Models/IUnitConfiguration";
import { PropertyConstants } from "../../../../Models/Constants";
import logger from '../../../../Services/LoggingService';

/**
 * Event handler functions for UnitConfigurationEditor
 */

/**
 * Handle selection change for combo boxes (inputs, outputs, PHE types)
 */
export const createHandleComboBoxChange = (
  handleInputOutputChange: (property: string, value: string, selected: boolean) => void,
  handlePHETypeChange: (value: string, selected: boolean) => void
) => (property: string, value: string, selected: boolean) => {
  if (property === PropertyConstants.Inputs || property === PropertyConstants.Outputs) {
    handleInputOutputChange(property, value, selected);
  } else if (property === PropertyConstants.PHEType) {
    handlePHETypeChange(value, selected);
  }
};

/**
 * Handle changes to inputs and outputs
 */
export const createHandleInputOutputChange = (
  section: IMultiSectionUnitConfiguration,
  setSection: (section: IMultiSectionUnitConfiguration) => void
) => (property: string, value: string, selected: boolean) => {
  const isInputs = property === PropertyConstants.Inputs;
  const currentValues = isInputs ? [...(section.inputs || [])] : [...(section.outputs || [])];
  
  if (selected) {
    if (!currentValues.includes(value)) {
      currentValues.push(value);
    }
  } else {
    const index = currentValues.indexOf(value);
    if (index !== -1) {
      currentValues.splice(index, 1);
    }
  }
  
  const updatedSection = {
    ...section,
    [property]: currentValues
  };
  
  setSection(updatedSection);
};

/**
 * Handle changes to PHE types
 */
export const createHandlePHETypeChange = (
  selectedPHETypes: string[],
  setSelectedPHETypes: (types: string[]) => void,
  unitConfig: IUnitBaseConfiguration,
  setUnitConfig: (config: IUnitBaseConfiguration) => void,
  sectionCollection: IMultiSectionUnitConfiguration[],
  setSectionCollection: (collection: IMultiSectionUnitConfiguration[]) => void
) => (value: string, selected: boolean) => {
  // Update selected PHE types
  let newTypes = [...selectedPHETypes];
  
  if (selected) {
    if (!newTypes.includes(value)) {
      newTypes.push(value);
    }
  } else {
    newTypes = newTypes.filter(type => type !== value);
  }
  
  setSelectedPHETypes(newTypes);
  setUnitConfig({ ...unitConfig, pheTypes: newTypes });
  
  // Update PHE types in all sections
  const updatedSections = sectionCollection.map(currentSection => {
    const sectionPHETypes = [...(currentSection.pheTypes || [])];
    
    if (selected && !sectionPHETypes.includes(value)) {
      sectionPHETypes.push(value);
    } else if (!selected) {
      const index = sectionPHETypes.indexOf(value);
      if (index !== -1) {
        sectionPHETypes.splice(index, 1);
      }
    }
    
    return {
      ...currentSection,
      pheTypes: sectionPHETypes
    };
  });
  
  setSectionCollection(updatedSections);
};

/**
 * Handle changes to unit configuration base properties
 */
export const createHandleBaseChange = (
  unitConfig: IUnitBaseConfiguration,
  setUnitConfig: (config: IUnitBaseConfiguration) => void,
  originalBasicDetails?: IUnitBaseConfiguration
) => (propertyName: string, value: string | boolean) => {
  const stringValue = String(value).replace('/', '');
  const currentConfig = { ...unitConfig };
  
  // Track changes to basic details if in edit mode
  if (originalBasicDetails) {
    const detailUpdated = checkIfBasicDetailUpdated(propertyName, originalBasicDetails, unitConfig);
    
    if (detailUpdated) {
      currentConfig.basicDetailsUpdated = true;
    }
  }
  
  setUnitConfig({ 
    ...currentConfig, 
    [propertyName]: stringValue 
  });
};

/**
 * Check if a basic detail property has been updated
 */
export const checkIfBasicDetailUpdated = (
  propertyName: string, 
  originalDetails: IUnitBaseConfiguration,
  unitConfig: IUnitBaseConfiguration
): boolean => {
  const fieldMap: Record<string, string> = {
    [PropertyConstants.Industry]: 'oldIndustry',
    [PropertyConstants.SubIndustry]: 'oldSubIndustry',
    [PropertyConstants.Application]: 'oldApplication',
    [PropertyConstants.SubApplication]: 'oldSubApplication',
    [PropertyConstants.ProductLine]: 'oldProductLine',
    [PropertyConstants.AlfaLavalPosition]: 'oldAlfaLavalPosition',
    [PropertyConstants.ProcessStage]: 'oldProcessStage'
  };
  
  const oldFieldName = fieldMap[propertyName];
  if (oldFieldName && !unitConfig[oldFieldName as keyof IUnitBaseConfiguration]) {
    const originalValue = originalDetails[propertyName as keyof IUnitBaseConfiguration] as string;
    (unitConfig as any)[oldFieldName] = originalValue;
    return true;
  }
  
  return false;
};

/**
 * Handle changes to multi-section configuration
 */
export const createHandleMultiSectionChange = (
  sectionCollection: IMultiSectionUnitConfiguration[],
  setSectionCollection: (collection: IMultiSectionUnitConfiguration[]) => void,
  flowchartSelectableProperties: string[],
  setFlowchartSelectableProperties: (properties: string[]) => void
) => (property: string, value: any, index: number) => {
  let updatedSelectableProperties = [...flowchartSelectableProperties];
  const sectionIndex = sectionCollection.findIndex(x => x.sectionNumber === index);
  let updatedSection = sectionIndex !== -1 
    ? { ...sectionCollection[sectionIndex] } 
    : { sectionNumber: index, inputs: [], outputs: [], coldMediaType: "", hotMediaType: "" };
      
  if (property === PropertyConstants.Inputs || property === PropertyConstants.Outputs) {
    updatedSection = handleMultiSectionInputOutput(
      property, 
      value, 
      index, 
      updatedSection, 
      updatedSelectableProperties
    );
  } else {
    updatedSection = { ...updatedSection, [property]: value };
  }
  
  const updatedCollection = [...sectionCollection];
  
  if (sectionIndex !== -1) {
    updatedCollection[sectionIndex] = updatedSection;
  } else {
    updatedCollection.push(updatedSection);
  }
  
  setSectionCollection(updatedCollection);
  setFlowchartSelectableProperties(updatedSelectableProperties);
};

/**
 * Handle input/output changes for multi-section configuration
 */
export const handleMultiSectionInputOutput = (
  property: string, 
  value: string[] | { key: string, selected: boolean }, 
  sectionIndex: number,
  currentSection: IMultiSectionUnitConfiguration,
  selectableProperties: string[]
) => {
  const isInputs = property === PropertyConstants.Inputs;
  
  // Handle array format from MultiSectionUnitConfiguration component
  if (Array.isArray(value)) {
    // Update selectable properties for flowchart
    const existingValues = isInputs ? [...(currentSection.inputs || [])] : [...(currentSection.outputs || [])];
    
    // Add new properties that don't exist in the current values
    value.forEach(key => {
      const sectionPropertyName = `${key}Sec${sectionIndex}`;
      if (!existingValues.includes(key) && !selectableProperties.includes(sectionPropertyName)) {
        selectableProperties.push(sectionPropertyName);
      }
    });
    
    // Remove properties that existed but were removed
    existingValues.forEach(key => {
      if (!value.includes(key)) {
        const sectionPropertyName = `${key}Sec${sectionIndex}`;
        const propIndex = selectableProperties.indexOf(sectionPropertyName);
        if (propIndex !== -1) {
          selectableProperties.splice(propIndex, 1);
        }
      }
    });
    
    // Return updated section with the new array
    return {
      ...currentSection,
      [property]: value
    };
  } 
  // Handle legacy format from older components
  else {
    const propertyKey = value.key;
    const isSelected = value.selected;
    const sectionPropertyName = `${propertyKey}Sec${sectionIndex}`;
    
    // Ensure arrays are initialized
    const currentValues = isInputs 
      ? [...(currentSection.inputs || [])]
      : [...(currentSection.outputs || [])];
    
    // Update the values array
    if (isSelected) {
      if (!currentValues.includes(propertyKey)) {
        currentValues.push(propertyKey);
        
        if (!selectableProperties.includes(sectionPropertyName)) {
          selectableProperties.push(sectionPropertyName);
        }
      }
    } else {
      const index = currentValues.indexOf(propertyKey);
      if (index !== -1) {
        currentValues.splice(index, 1);
        
        const propIndex = selectableProperties.indexOf(sectionPropertyName);
        if (propIndex !== -1) {
          selectableProperties.splice(propIndex, 1);
        }
      }
    }
    
    // Return updated section
    return {
      ...currentSection,
      [property]: currentValues
    };
  }
};

/**
 * Set the number of sections for the configuration
 */
export const createHandleSetSectionCount = (
  setAmountOfSections: (count: number) => void,
  sectionCollection: IMultiSectionUnitConfiguration[],
  setSectionCollection: (collection: IMultiSectionUnitConfiguration[]) => void,
  selectedPHETypes: string[]
) => (count: number) => {
  setAmountOfSections(count);
  
  // Prepare an updated section collection with the specified count
  const updatedCollection: IMultiSectionUnitConfiguration[] = [];
  
  for (let i = count; i > 0; i--) {
    const existingSection = sectionCollection.find(x => x.sectionNumber === i);
    
    if (existingSection) {
      updatedCollection.push(existingSection);
    } else {
      updatedCollection.push({ 
        sectionNumber: i, 
        inputs: [], 
        outputs: [], 
        coldMediaType: "", 
        hotMediaType: "",
        pheTypes: [...selectedPHETypes] // Add PHE types to new sections
      });
    }
  }
  
  logger.debug('Setting section collection', {
    sectionCount: count,
    sectionsData: updatedCollection.map(s => ({ 
      sectionNumber: s.sectionNumber, 
      inputsCount: s.inputs?.length || 0,
      outputsCount: s.outputs?.length || 0
    }))
  }, {
    component: 'UnitConfigurationEditor',
    method: 'handleSetSectionCount'
  });
  setSectionCollection(updatedCollection);
};

/**
 * Handle PHE type selection change for UI components
 */
export const createHandlePHETypeSelectionChange = (
  setSelectedPHETypes: (types: string[]) => void,
  unitConfig: IUnitBaseConfiguration,
  setUnitConfig: (config: IUnitBaseConfiguration) => void
) => (newTypes: string[]) => {
  setSelectedPHETypes(newTypes);
  setUnitConfig({ ...unitConfig, pheTypes: newTypes });
}; 